import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import classNames from 'classnames';
import { rewritePortalLinks } from 'src/utils/portalLinks';
import styles from './AiChat.module.scss';

marked.use({ gfm: true, breaks: true });

const isInternalHref = (href: string | null): boolean => !!href && href.startsWith('/') && !href.startsWith('//');

DOMPurify.addHook('afterSanitizeAttributes', node => {
  if (node.tagName !== 'A') return;
  if (isInternalHref(node.getAttribute('href'))) {
    node.setAttribute('data-internal', '1');
    node.removeAttribute('target');
    return;
  }
  node.setAttribute('target', '_blank');
  node.setAttribute('rel', 'noreferrer');
});

/**
 * Renders assistant markdown (bold, lists, links, GFM tables) sanitized against the design's block styles.
 * `portal://` links from the assistant become in-app routes and navigate without a page reload.
 */
const Markdown: React.FC<{ content: string; className?: string }> = ({ content, className }) => {
  const navigate = useNavigate();
  const html = useMemo(
    () => DOMPurify.sanitize(marked.parse(rewritePortalLinks(content), { async: false }) as string),
    [content],
  );

  const onClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const anchor = (e.target as HTMLElement).closest('a');
    if (!anchor || anchor.getAttribute('data-internal') !== '1') return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    navigate(anchor.getAttribute('href') as string);
  };

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
    <div
      className={classNames(styles.markdown, 'typo-text-14 text-full', className)}
      onClick={onClick}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

export default Markdown;
